import { ACADEMY_COURSE_IDS, ACADEMY_OFFER_IDS, EXPLORE_VIDEOS, ONBOARDING_STEPS, videoLanguage } from "../core";

const STEPS_WITH_EVIDENCE = Object.freeze([
  "sectors", "enclosures", "individuals", "groups", "groupMembers",
  "fastEvaluation", "team", "campaigns", "dailyReports", "tasks", "enrichments",
]);

const RECOMMENDATIONS = Object.freeze([
  "zimsImport", "groupsWithoutIndividuals", "populationGroups", "reevaluate", "mobileApp", "inviteEarly",
]);

const RECOMMENDATIONS_WITH_ACTION = Object.freeze(["zimsImport", "groupsWithoutIndividuals", "reevaluate", "inviteEarly"]);

// Les thèmes du parc reprennent les parties du parcours ; « interface » est propre à l'académie.
export const courseKey = (id) => (id === "interface" ? `onboardingV2.academy.courses.${id}` : `onboardingV2.phases.${id}`);

/**
 * Toutes les chaînes de la page « Bien démarrer ». Les compteurs passent par
 * la pluralisation d'i18next (`count`), les nombres par Intl.
 */
export const buildGettingStartedMessages = (t, language = "fr") => {
  const number = new Intl.NumberFormat(language || "fr");
  const list = (items) => (typeof Intl.ListFormat === "function"
    ? new Intl.ListFormat(language || "fr", { style: "long", type: "conjunction" }).format(items)
    : items.join(", "));
  const key = (suffix) => `onboardingV2.${suffix}`;
  const academy = (suffix) => `onboardingV2.academy.${suffix}`;

  const steps = Object.fromEntries(ONBOARDING_STEPS.map(({ id }) => [id, {
    title: t(key(`steps.${id}.title`)),
    description: t(key(`steps.${id}.description`)),
    ...(id === "groups" ? { variants: { population: t(key("steps.groups.populationDescription")) } } : {}),
    ...(STEPS_WITH_EVIDENCE.includes(id)
      ? { evidence: (count) => t(key(`steps.${id}.evidence`), { count, value: number.format(count) }) }
      : {}),
  }]));

  const recommendations = Object.fromEntries(RECOMMENDATIONS.map((id) => [id, {
    title: t(key(`recommendations.${id}.title`)),
    description: (item) => t(key(`recommendations.${id}.description`), { count: item?.days, days: item?.days }),
    ...(RECOMMENDATIONS_WITH_ACTION.includes(id) ? { action: t(key(`recommendations.${id}.action`)) } : {}),
  }]));

  return {
    page: {
      ariaLabel: t(key("page.ariaLabel")),
      eyebrow: t(key("page.eyebrow")),
      title: t(key("page.title")),
      description: t(key("page.description")),
    },
    courses: Object.fromEntries(ACADEMY_COURSE_IDS.map((id) => [id, {
      title: t(`${courseKey(id)}.title`),
      description: t(`${courseKey(id)}.description`),
    }])),
    academy: {
      percent: (value) => t(academy("percent"), { value: number.format(value) }),
      coursesTitle: t(academy("coursesTitle")),
      progress: {
        label: t(academy("progress.label")),
        value: (done, total) => t(academy("progress.value"), { done: number.format(done), total: number.format(total) }),
        summary: (done, total) => t(academy("progress.summary"), { count: done, done: number.format(done), total: number.format(total) }),
      },
      resume: {
        eyebrow: (course) => t(academy("resume.eyebrow"), { course }),
        course: t(academy("resume.course")),
      },
      complete: {
        eyebrow: t(academy("complete.eyebrow")),
        title: t(academy("complete.title")),
        description: t(academy("complete.description")),
      },
      course: {
        number: (value) => t(academy("course.number"), { value: number.format(value) }),
        posterTag: (value) => t(academy("course.posterTag"), { value: number.format(value) }),
        lessons: (count) => t(academy("course.lessons"), { count, value: number.format(count) }),
        minutes: (value) => t(academy("course.minutes"), { value: number.format(value) }),
        progress: (done, total) => t(academy("course.progress"), { count: done, done: number.format(done), total: number.format(total) }),
        progressLabel: (course) => t(academy("course.progressLabel"), { course }),
        done: t(academy("course.done")),
        start: t(academy("course.start")),
        continue: t(academy("course.continue")),
        review: t(academy("course.review")),
        back: t(academy("course.back")),
        next: (course) => t(academy("course.next"), { course }),
        curriculum: t(academy("course.curriculum")),
      },
      lesson: {
        number: (value) => t(academy("lesson.number"), { value: number.format(value) }),
        tourMeta: (minutes) => t(academy("lesson.tourMeta"), { value: number.format(minutes) }),
        practiceOnly: t(academy("lesson.practiceOnly")),
        tourSoon: t(academy("lesson.tourSoon")),
        play: t(academy("lesson.play")),
        rate: t(academy("lesson.rate")),
        replay: t(academy("lesson.replay")),
        open: t(academy("lesson.open")),
        inPark: (evidence) => t(academy("lesson.inPark"), { evidence }),
        pending: t(academy("lesson.pending")),
        learnedPending: t(academy("lesson.learnedPending")),
        completed: t(academy("lesson.completed")),
      },
      video: {
        // Langue de la vidéo : le français pour une interface en français, l'anglais sinon.
        language: videoLanguage(language),
        englishOnly: videoLanguage(language) === "en" && !/^en(?:[-_]|$)/i.test(String(language || "")) ? t(academy("video.englishOnly")) : null,
        eyebrow: t(academy("video.eyebrow")),
        title: t(academy("video.title")),
        description: t(academy("video.description")),
        play: (duration) => t(academy("video.play"), { duration }),
        duration: (seconds) => t(academy("video.duration"), {
          minutes: number.format(Math.floor(seconds / 60)),
          seconds: String(seconds % 60).padStart(2, "0"),
        }),
        frameTitle: (title) => t(academy("video.frameTitle"), { title }),
        watch: t(academy("video.watch")),
        number: (value) => t(academy("video.number"), { value: number.format(value) }),
        explore: Object.fromEntries(EXPLORE_VIDEOS.map(({ id }) => [id, t(academy(`video.explore.${id}`))])),
      },
      // Les offres qui incluent chaque module (noms officiels du catalogue), le
      // filtre par offre et la leçon verrouillée hors de l'offre du parc.
      offers: {
        names: Object.fromEntries(ACADEMY_OFFER_IDS.map((id) => [id, t(`productFeatureCatalog.offers.${id}`)])),
        filterLabel: t(academy("offers.filterLabel")),
        all: t(academy("offers.all")),
        yours: (offers = []) => t(academy("offers.yours"), { count: offers.length, offers: list(offers) }),
        hint: t(academy("offers.hint")),
        includedIn: (offers = []) => t(academy("offers.includedIn"), { count: offers.length, offers: list(offers) }),
        everyOffer: t(academy("offers.everyOffer")),
        locked: t(academy("offers.locked")),
        lockedLessons: (offers = []) => t(academy("offers.lockedLessons"), { count: offers.length, offers: list(offers) }),
        lockedCourse: t(academy("offers.lockedCourse")),
        lessons: t(academy("offers.lessons")),
        emptyTitle: t(academy("offers.emptyTitle")),
        emptyDescription: t(academy("offers.emptyDescription")),
      },
      // La page d'un tuto : sa vidéo, puis ses leçons pas à pas (visites guidées
      // qui refont les gestes de la vidéo dans l'interface).
      tutorial: {
        back: t(academy("tutorial.back")),
        next: (title) => t(academy("tutorial.next"), { title }),
        videoTitle: t(academy("tutorial.videoTitle")),
        videoDescription: t(academy("tutorial.videoDescription")),
      },
    },
    steps,
    status: {
      done: t(key("status.done")),
      next: t(key("status.next")),
      blocked: (titles) => t(key("status.blocked"), { steps: list(titles) }),
      delegated: t(key("status.delegated")),
      skipped: t(key("status.skipped")),
      optional: t(key("status.optional")),
      unknown: t(key("status.unknown")),
    },
    actions: {
      skip: t(key("actions.skip")),
      restore: t(key("actions.restore")),
      retry: t(key("actions.retry")),
    },
    recommendations: { title: t(key("recommendations.title")), ...recommendations },
    tours: {
      tourTitle: (tourId) => t(`navigationV2.tours.catalogue.${tourId}.title`),
      tourDescription: (tourId) => t(`navigationV2.tours.catalogue.${tourId}.description`),
    },
    help: {
      title: t(key("help.title")),
      description: t(key("help.description")),
      center: t(key("help.center")),
      support: t(key("help.support")),
    },
    states: {
      errorTitle: t(key("states.errorTitle")),
      errorDescription: t(key("states.errorDescription")),
      emptyTitle: t(key("states.emptyTitle")),
      emptyDescription: t(key("states.emptyDescription")),
    },
  };
};
